/**
 * POR QUE A MÉTRICA DO EC2 NÃO TEM NÚMERO (23/10/2026, backend #530).
 *
 * A tela dizia "CloudWatch Agent não instalado" sempre que a memória ou o disco vinham `null`. A causa era deduzida da
 * ausência do número, e estava errada por sete meses: o agente estava ativo, e o que faltava era a credencial do backend.
 * Agora o backend manda o motivo junto (`diagnostico.memoria` / `diagnostico.disco`), e a tela só repete o que ele disse.
 *
 * ⛔ A tela nunca diagnostica. Sem `diagnostico` (backend antigo, ou a resposta sem o campo), a frase é "sem dado", sem
 * causa: ausência de número não autoriza diagnóstico.
 */
export type MotivoDaAusencia =
  | 'instancia-nao-configurada'
  | 'credencial-invalida'
  | 'sem-permissao'
  | 'metrica-nao-publicada'
  | 'dimensao-nao-encontrada'
  | 'sem-dado-na-janela'
  | 'erro-ao-consultar';

export interface DiagnosticoDaMetrica {
  temDado: boolean;
  motivo: MotivoDaAusencia | null;
  /** A frase do backend, com o que se sabe. */
  detalhe: string | null;
  /** `null` = não se sabe. */
  agenteInstalado: boolean | null;
}

export interface DiagnosticoDoMonitor {
  memoria?: DiagnosticoDaMetrica | null;
  disco?: DiagnosticoDaMetrica | null;
}

export const SEM_DADO = 'sem dado';

/** A frase para a métrica sem número: a do backend, ou "sem dado" quando ele não disse. */
export function fraseDaAusencia(diag: DiagnosticoDaMetrica | null | undefined): string {
  const detalhe = diag && typeof diag.detalhe === 'string' ? diag.detalhe.trim() : '';
  return detalhe || SEM_DADO;
}

export interface LinhaDoMotivo {
  chave: 'memoria' | 'disco' | 'memoria-e-disco';
  rotulo: string;
  frase: string;
}

/**
 * As linhas de motivo do cartão (J1, Heitor 23/10/2026): memória e disco sem número com a MESMA frase viram uma linha só,
 * "Memória e disco"; com frases diferentes, uma linha para cada. Métrica com número não tem linha.
 */
export function linhasDoMotivo(semMemoria: boolean, semDisco: boolean, diag: DiagnosticoDoMonitor | null | undefined): LinhaDoMotivo[] {
  const mem = semMemoria ? fraseDaAusencia(diag?.memoria) : null;
  const disco = semDisco ? fraseDaAusencia(diag?.disco) : null;
  if (mem !== null && disco !== null && mem === disco) return [{ chave: 'memoria-e-disco', rotulo: 'Memória e disco', frase: mem }];
  return [
    ...(mem !== null ? [{ chave: 'memoria' as const, rotulo: 'Memória', frase: mem }] : []),
    ...(disco !== null ? [{ chave: 'disco' as const, rotulo: 'Disco', frase: disco }] : []),
  ];
}
